import {
  View,
  Text,
  ScrollView,
  ActivityIndicator,
  BackHandler,
  Alert,
  Platform,
} from "react-native";
import React, { useEffect, version, useState } from "react";
import Constants from "expo-constants";
import { Toast } from "react-native-toast-notifications";
import i18n from "../../../locales/i18n";
import StandardNavigation from "../../../components/navigation/StandardNavigation";
import StartComp from "../../../components/StartComp";
import DynamicStatsCard from "../../../components/screens/start-screen/DynamicStatsCard";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../store";
import customAxios from "../../../utils/customAxios";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { authActions } from "../../../store/slices/authSlice";
import { useLanguage } from "../../../hooks/useLanguage";
import { useFocusEffect } from "@react-navigation/native";
import StockImage from "../../../assets/images/stock.png";
import Animated, { FadeInDown, FadeIn, Easing, withSpring } from "react-native-reanimated";

import SellImage from "../../../assets/images/sell.svg";
import DeptImage from "../../../assets/images/dept.svg";
import StatsImage from "../../../assets/images/stats.svg";
import SettingsImage from "../../../assets/images/settings.svg";
import OrdersImage from "../../../assets/images/orders.svg";
import { useStock } from "../../../hooks/useStock";
import WebStartScreen from "../../../components/web/WebStartScreen";

const StartScreen = () => {
  // Move START_COMP_ITEMS inside component so translations update when language changes
  const START_COMP_ITEMS = [
    {
      title: i18n.t("stock"),
      iconSource: StockImage, // Using PNG image instead of SVG component
      url: "stock",
    },
    {
      title: i18n.t("sell"),
      IconComponent: SellImage,
      url: "scanner", // Will be dynamic based on sellWithScanner
      urlWithoutScanner: "scannerwithout",
    },
    {
      title: i18n.t("bills"),
      IconComponent: StatsImage,
      url: "bills",
    },
    {
      title: i18n.t("credit"),
      IconComponent: DeptImage,
      url: "dettes",
    },
    {
      title: i18n.t("orders"),
      IconComponent: OrdersImage,
      url: "orders",
    },
    {
      title: i18n.t("settings"),
      IconComponent: SettingsImage,
      url: "params",
    },
  ];
  const dispatch = useDispatch();
  const { user, refetch } = useSelector((state: RootState) => state.auth);
  const { sellWithScanner } = useSelector((state: RootState) => state.app);

  // State for double back to exit functionality
  const [backPressCount, setBackPressCount] = useState(0);

  // Use the language hook for automatic i18n sync
  const { currentLanguage } = useLanguage();

  // Handle hardware back button ONLY when this screen is focused
  useFocusEffect(
    React.useCallback(() => {
      const backAction = () => {
        if (backPressCount === 0) {
          setBackPressCount(1);
          Toast.show(
            i18n.t("pressBackAgainToExit") || "Press back again to exit",
            {
              type: "info",
              duration: 2000,
            }
          );

          // Reset back press count after 2 seconds
          setTimeout(() => {
            setBackPressCount(0);
          }, 2000);

          return true; // Prevent default behavior
        } else {
          // Second press within 2 seconds - exit app
          BackHandler.exitApp();
          return false;
        }
      };

      const backHandler = BackHandler.addEventListener(
        "hardwareBackPress",
        backAction
      );

      // Cleanup when screen loses focus
      return () => backHandler.remove();
    }, [backPressCount])
  );

  // Don't force redirect - let users use the app without verification
  // They can verify later through profile settings
  useEffect(() => {
    if (user?.is_verified === false) {
      // Show a gentle reminder instead of forcing redirect
      Toast.show(i18n.t("verifyEmailReminder"), {
        type: "info",
        duration: 5000,
      });
    }
  }, [user]);

  // Preload stock data on start page
  const { fetchProducts, isLoading: stockLoading, products } = useStock();

  const appVersion = Constants.expoConfig?.version;

  // Remove duplicate auth check since it's now handled in app/index.tsx
  // Only refetch user data when refetch flag changes (like after login)
  useEffect(() => {
    if (refetch) {
      const fetchUserData = async () => {
        try {
          const token = await AsyncStorage.getItem("accessToken");
          if (!token) {
            dispatch(authActions.setUser(null));
            return;
          }

          const res = await customAxios.get("/users/check");
          dispatch(authActions.setUser(res.data.userinfo));
        } catch (error) {
          await AsyncStorage.removeItem("accessToken");
          dispatch(authActions.setUser(null));
        }
      };
      fetchUserData();
    }
  }, [dispatch, refetch]);

  // Preload stock data when start screen loads
  useEffect(() => {
    fetchProducts(true); // Force refresh on start screen load
  }, []);

  // Language is now handled by useLanguage hook - no need for manual sync

  useEffect(() => {
    const checkAppUpdate = async () => {
      try {
        const response = await customAxios.post("/checkupdate", {
          version: appVersion,
        });
        if (response.data.message == "Update available") {
          Toast.show(i18n.t("newVersionAvailable"), {
            type: "success",
            duration: 5000,
            animationType: "zoom-in",
          });
        }
      } catch (error) {
        return null;
      }
    };
    checkAppUpdate();
  }, []);

  // On web, render the web-specific dashboard layout
  if (Platform.OS === "web") {
    return <WebStartScreen />;
  }

  // Mobile layout (unchanged)
  return (
    <View style={{ flex: 1, backgroundColor: "#f5f5f5" }}>
      {/* Standard Navigation */}
      <StandardNavigation
        showBackButton={true}
        showMenuButton={true}
        showLogo={true}
      />
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={{ flex: 1, padding: 20 }}
      >
        <View className=" flex-1  justify-center w-full  ">
          <View
            style={{ marginBottom: 20 }}
            className="flex items-center justify-center w-full "
          >
            <Animated.Text
              entering={FadeIn.duration(400)}
              className="text-2xl text-primaryV2 mt-2 font-semibold"
            >
              {i18n.t("choice")}
            </Animated.Text>

            {stockLoading ? (
              <View
                className="w-full bg-white rounded-xl p-4 mb-4 items-center justify-center"
                style={{
                  shadowColor: "#000",
                  shadowOffset: { width: 0, height: 2 },
                  shadowOpacity: 0.1,
                  shadowRadius: 8,
                  elevation: 5,
                }}
              >
                <ActivityIndicator size="small" color="#0596D0" />
                <Text className="text-gray-600 text-sm mt-2">
                  {i18n.t("loading")}
                </Text>
              </View>
            ) : (
              <DynamicStatsCard key={`stats-${products.length}`} />
            )}

            <Animated.View
              entering={FadeInDown.duration(600).delay(100)}
              className="flex flex-row items-center justify-center gap-4 py-2"
            >
              <StartComp
                key="start-stock"
                url={START_COMP_ITEMS[0].url}
                IconComponent={START_COMP_ITEMS[0].IconComponent}
                iconSource={START_COMP_ITEMS[0].iconSource}
                title={START_COMP_ITEMS[0].title}
              />
              <StartComp
                key="start-sell"
                IconComponent={START_COMP_ITEMS[1].IconComponent}
                iconSource={START_COMP_ITEMS[1].iconSource}
                url={
                  sellWithScanner
                    ? START_COMP_ITEMS[1].url
                    : START_COMP_ITEMS[1].urlWithoutScanner!
                }
                title={START_COMP_ITEMS[1].title}
              />
            </Animated.View>
            <Animated.View
              entering={FadeInDown.duration(600).delay(200)}
              className="flex flex-row items-center justify-center gap-4 py-2"
            >
              <StartComp
                key="start-bills"
                IconComponent={START_COMP_ITEMS[2].IconComponent}
                iconSource={START_COMP_ITEMS[2].iconSource}
                url={START_COMP_ITEMS[2].url}
                title={START_COMP_ITEMS[2].title}
              />
              <StartComp
                key="start-credit"
                IconComponent={START_COMP_ITEMS[3].IconComponent}
                iconSource={START_COMP_ITEMS[3].iconSource}
                url={START_COMP_ITEMS[3].url}
                title={START_COMP_ITEMS[3].title}
              />
            </Animated.View>
            <Animated.View
              entering={FadeInDown.duration(600).delay(300)}
              className="flex flex-row items-center justify-center gap-4 py-2"
            >
              <StartComp
                key="start-fourni"
                IconComponent={START_COMP_ITEMS[4].IconComponent}
                iconSource={START_COMP_ITEMS[4].iconSource}
                url={START_COMP_ITEMS[4].url}
                title={START_COMP_ITEMS[4].title}
              />
              <StartComp
                key="start-settings"
                IconComponent={START_COMP_ITEMS[5].IconComponent}
                iconSource={START_COMP_ITEMS[5].iconSource}
                url={START_COMP_ITEMS[5].url}
                title={START_COMP_ITEMS[5].title}
              />
            </Animated.View>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

export default StartScreen;
